import { useState } from "react";
import { Cookie } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";

const KEY = "hc_consent";

export function getConsent() {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "granted" || v === "denied") return v;
  } catch (_e) {
    // ignore
  }
  return null;
}

export default function CookieConsent() {
  const { lang } = useLanguage();
  const [decision, setDecision] = useState(() => getConsent());

  const set = (val) => {
    try {
      localStorage.setItem(KEY, val);
    } catch (_e) {
      // ignore
    }
    setDecision(val);
    // Emit event so analytics scripts (when wired) can react
    window.dispatchEvent(new CustomEvent("hc:consent", { detail: val }));
  };

  if (decision === "granted" || decision === "denied") return null;

  const copy =
    lang === "es"
      ? {
          title: "Esta página usa cookies",
          body: "Usamos cookies para mejorar tu experiencia y entender qué actividades buscas. Puedes aceptar o rechazar las cookies de análisis.",
          accept: "Aceptar todas",
          decline: "Solo necesarias",
          learn: "Política de privacidad",
        }
      : {
          title: "This site uses cookies",
          body: "We use cookies to improve your experience and understand which activities you're after. You can accept or reject analytics cookies.",
          accept: "Accept all",
          decline: "Necessary only",
          learn: "Privacy Policy",
        };

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-live="polite"
      aria-label={copy.title}
      className="fixed bottom-3 left-3 right-3 z-40 mx-auto max-w-md rounded-2xl border border-[#0A0A0A]/10 bg-white p-4 shadow-2xl shadow-black/15 sm:bottom-4 sm:left-4 sm:right-4 sm:max-w-lg sm:p-5 md:bottom-6 md:left-6 md:right-6 md:max-w-3xl md:p-6 lg:flex lg:items-center lg:gap-6"
    >
      <div className="flex items-start gap-3 lg:flex-1 lg:items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FF5533]/10 text-[#FF5533] sm:h-10 sm:w-10">
          <Cookie size={16} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className="text-[15px] font-semibold leading-tight text-[#0A0A0A] sm:text-base"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            {copy.title}
          </h3>
          <p className="mt-1 text-[13px] leading-relaxed text-[#4A4A4A] sm:text-sm">
            {copy.body}{" "}
            <Link
              to="/privacy"
              className="font-medium text-[#FF5533] underline-offset-4 hover:underline"
            >
              {copy.learn}
            </Link>
            .
          </p>
        </div>
      </div>
      <div className="mt-4 grid w-full shrink-0 grid-cols-2 gap-2 lg:mt-0 lg:flex lg:w-auto lg:grid-cols-none">
        <button
          type="button"
          onClick={() => set("denied")}
          data-testid="cookie-decline"
          className="rounded-full border border-[#0A0A0A]/15 bg-white px-4 py-2.5 text-[13px] font-semibold text-[#0A0A0A] transition-colors hover:border-[#0A0A0A]/30 sm:text-sm lg:px-5"
        >
          {copy.decline}
        </button>
        <button
          type="button"
          onClick={() => set("granted")}
          data-testid="cookie-accept"
          className="rounded-full bg-[#0A0A0A] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#FF5533] sm:text-sm lg:px-5"
        >
          {copy.accept}
        </button>
      </div>
    </div>
  );
}
